import {loadStripe} from '@stripe/stripe-js';
import {$qsAll} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import {type PaymentData} from '../../../../@type/stripe';
import {checkRequiredFields} from '../../features/additionalFields';
import {updateCustomerDataInBrowser} from '../../features/customer';
import {type IStripeContext, type StripeGateway} from '../../models/stripe';
import {Feature, startModalProcessing, stopModalLoading} from '../../reducers/environmentReducer';
import {PaymentConfiguration, registerGatewayBatch} from '../../reducers/paymentConfigurationReducer';
import {updateCustomerPreferredGateway} from '../../reducers/peachPayCustomerReducer';
import {PeachPayOrder} from '../../reducers/peachPayOrderReducer';
import {store} from '../../store';
import {clearLocalSession} from '../session';
import {setupStripeButton} from './button';
import {initStripeACHDebitMethod} from './gateways/achDebit';
import {initStripeAffirmMethod} from './gateways/affirm';
import {initStripeAfterPayMethod} from './gateways/afterpay';
import {initStripeApplepayMethod} from './gateways/applepay';
import {initStripeBancontactMethod} from './gateways/bancontact';
import {initStripeCardMethod} from './gateways/card';
import {initStripeEPSMethod} from './gateways/eps';
import {initStripeGiropayMethod} from './gateways/giropay';
import {initStripeGooglepayMethod} from './gateways/googlepay';
import {initStripeIdealMethod} from './gateways/ideal';
import {initStripeKlarnaMethod} from './gateways/klarna';
import {initStripeP24Method} from './gateways/p24';
import {initStripeSepaDebitMethod} from './gateways/sepaDebit';
import {initStripeSofortMethod} from './gateways/sofort';
import {displayPaymentErrorMessage, type OrderService} from '../order';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';

export default async function initStripePaymentIntegration(orderService: OrderService) {
	const publicKey = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'public_key');
	const connectId = Feature.metadata<string>(FeatureFlag.STRIPE_GATEWAYS, 'connect_id');

	if (!publicKey || !connectId) {
		return;
	}

	const stripe = await loadStripe(publicKey, {stripeAccount: connectId, locale: 'auto'});
	if (!stripe) {
		return;
	}

	const elements = stripe.elements();

	const context: IStripeContext = {
		stripe,
		elements,
	};

	// Internal Payment Methods
	const gateways = registerStripeGateways();
	await activateStripeGateways(context, gateways, orderService);
	setupStripeButton();
}

/**
 * Sets up each stripe payment gateway
 */
function registerStripeGateways(): Record<string, StripeGateway> {
	const gatewayImplementations: Record<string, StripeGateway> = {};
	const gatewayConfigurations: Record<string, GatewayConfiguration> = {};

	if (Feature.enabled(FeatureFlag.STRIPE_CARD_GATEWAY)) {
		const method = initStripeCardMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFFIRM_GATEWAY)) {
		const method = initStripeAffirmMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_KLARNA_GATEWAY)) {
		const method = initStripeKlarnaMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_AFTERPAY_GATEWAY)) {
		const method = initStripeAfterPayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SOFORT_GATEWAY)) {
		const method = initStripeSofortMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_ACHDEBIT_GATEWAY)) {
		const method = initStripeACHDebitMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_EPS_GATEWAY)) {
		const method = initStripeEPSMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_P24_GATEWAY)) {
		const method = initStripeP24Method();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_SEPADEBIT_GATEWAY)) {
		const method = initStripeSepaDebitMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_BANCONTACT_GATEWAY)) {
		const method = initStripeBancontactMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GIROPAY_GATEWAY)) {
		const method = initStripeGiropayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_IDEAL_GATEWAY)) {
		const method = initStripeIdealMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_APPLEPAY_GATEWAY)) {
		const method = initStripeApplepayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	if (Feature.enabled(FeatureFlag.STRIPE_GOOGLEPAY_GATEWAY)) {
		const method = initStripeGooglepayMethod();
		gatewayImplementations[method.config.gatewayId] = method;
		gatewayConfigurations[method.config.gatewayId] = method.config;
	}

	store.dispatch(registerGatewayBatch(gatewayConfigurations));

	return gatewayImplementations;
}

async function activateStripeGateways(context: IStripeContext, gateways: Record<string, StripeGateway>, orderService: OrderService) {
	const activations = [];
	for (const [_, method] of Object.entries(gateways)) {
		if (method.init) {
			activations.push(method.init(context));
		}
	}

	await Promise.all(activations);

	const confirm = (event: MouseEvent) => {
		const requiredFieldsVerification = checkRequiredFields();
		if (!requiredFieldsVerification) {
			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			return;
		}

		const gatewayId = PaymentConfiguration.selectedGateway();
		const gateway = gateways[gatewayId];
		if (!gateway) {
			return;
		}

		return paymentFlow(context, gateway, orderService);
	};

	$qsAll<HTMLElement>('.stripe-btn', $el => {
		$el.addEventListener('click', event => confirm(event));
	});
}

async function paymentFlow(context: IStripeContext, gateway: StripeGateway, orderService: OrderService): Promise<void> {
	store.dispatch(startModalProcessing());

	const [paymentId, update] = await gateway.tokenize(context, PeachPayOrder.sessionId());

	if (!paymentId && update.note) {
		displayPaymentErrorMessage(update.note ?? '');
	}

	const transaction = await orderService.startTransaction(gateway.config.gatewayId);
	if (!transaction) {
		store.dispatch(stopModalLoading());
		return;
	}

	if (!paymentId) {
		await transaction.complete(update);
		store.dispatch(stopModalLoading());
		return;
	}

	const order = await orderService.placeOrder(transaction, {
		peachpay_session_id: PeachPayOrder.sessionId(),
		peachpay_transaction_id: transaction.getId(),
		peachpay_stripe_payment_method_id: paymentId,
	});

	if (order.result === 'failure') {
		await transaction?.complete();
		store.dispatch(stopModalLoading());
		return;
	}

	const dataURL = new URL(order.redirect);

	if (dataURL.hash === '') {
		await transaction.complete();

		store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

		updateCustomerDataInBrowser();
		clearLocalSession();

		// @ts-expect-error: window.top is always existent
		window.top.location.href = dataURL;
	}

	const [name, data] = dataURL.hash.split('=');
	if (name !== '#payment_data' || !data) {
		await transaction.complete();
		store.dispatch(stopModalLoading());
		return;
	}

	const paymentData = JSON.parse(atob(decodeURIComponent(data))) as PaymentData;

	const clientSecret = paymentData.data.client_secret;
	const intermediateURL = paymentData.intermediate_url;
	const successURL = paymentData.success_url;

	try {
		const [status, update] = await gateway.confirm(context, clientSecret, intermediateURL);

		if (!status && update.note) {
			displayPaymentErrorMessage(update.note ?? '');
		}

		await transaction.complete(update);
		if (!status) {
			store.dispatch(stopModalLoading());
			return;
		}

		store.dispatch(updateCustomerPreferredGateway(PaymentConfiguration.selectedGateway()));

		updateCustomerDataInBrowser();
		clearLocalSession();

		if (status === 'success') {
			// @ts-expect-error: window.top is always existent
			window.top.location.href = successURL;
		}
	} catch (error) {
		if (error instanceof Error) {
			await transaction.complete({
				note: error.message,
				paymentStatus: 'failed',
			});
		}

		store.dispatch(stopModalLoading());
	}
}
